// @ts-nocheck
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { supabase } from '@product-radar/shared';
import { useWorkspace, useRegionalSettings } from '@/contexts/context';
import { formatDate } from '@/lib/formatting';
import { hasPermission, getRoleDisplayName, type UserRole } from '@product-radar/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger, SheetBody, SheetFooter } from './ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { Label } from './ui/label';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { 
  Search, 
  Plus, 
  Mail,
  Shield,
  MoreHorizontal,
  Edit,
  Trash2,
  Crown,
  Eye,
  UserPlus,
  Loader2
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { RolePermissionsMatrix } from './role-permissions-matrix';

const SUPABASE_PROJECT_ID = import.meta.env.VITE_SUPABASE_PROJECT_ID;
const SUPABASE_URL = SUPABASE_PROJECT_ID ? `https://${SUPABASE_PROJECT_ID}.supabase.co` : null;
let invitationEmailSelectable = true;

const memberSelectFields = () => `
        id,
        workspace_id,
        user_id,
        role,
        status,
        product_id,
        invited_by,
        invited_at,
        joined_at,
        last_active_at,
        ${invitationEmailSelectable ? 'invitation_email,' : ''}
        profiles:user_id (
          id,
          email,
          full_name,
          avatar_url
        ),
        products:product_id (
          id,
          name,
          color
        )
      `;

interface Product {
  id: string;
  name: string;
  color: string;
}

interface WorkspaceMember {
  id: string;
  workspace_id: string;
  user_id: string;
  role: 'admin' | 'product_manager' | 'contributor' | 'viewer';
  status: 'active' | 'invited' | 'suspended';
  product_id: string | null;
  invited_by: string | null;
  invited_at: string | null;
  joined_at: string;
  last_active_at: string | null;
  invitation_email?: string | null;
  profiles: {
    id: string;
    email: string;
    full_name: string;
    avatar_url: string | null;
  };
  products?: {
    id: string;
    name: string;
    color: string;
  } | null;
}

const roleConfig = {
  'admin': {
    color: 'destructive' as const,
    icon: <Crown className="h-3 w-3" />,
    label: 'Admin'
  },
  'product_manager': {
    color: 'default' as const,
    icon: <Shield className="h-3 w-3" />,
    label: 'Product Manager'
  },
  'contributor': {
    color: 'secondary' as const,
    icon: <UserPlus className="h-3 w-3" />,
    label: 'Contributor'
  },
  'viewer': {
    color: 'outline' as const,
    icon: <Eye className="h-3 w-3" />,
    label: 'Viewer'
  }
};

export function UserManagement() {
  const { workspace, membership } = useWorkspace();
  const regionalSettings = useRegionalSettings();
  const userRole = membership?.role as UserRole;
  const isAdmin = userRole === 'admin';
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedMember, setSelectedMember] = useState<WorkspaceMember | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('viewer');
  const [inviteProductId, setInviteProductId] = useState<string>('all');
  const [isInviting, setIsInviting] = useState(false);
  const [editingMember, setEditingMember] = useState<{ memberId: string; newRole: UserRole; newProductId: string | null } | null>(null);
  const [isUpdatingMember, setIsUpdatingMember] = useState(false);
  const [resendingInvitationId, setResendingInvitationId] = useState<string | null>(null);

  useEffect(() => {
    if (workspace) {
      loadProducts();
      loadMembers();
    }
  }, [workspace]);

  const loadProducts = async () => {
    if (!workspace) return;

    try {
      // Use RPC function to bypass RLS
      const { data, error } = await supabase
        .rpc('get_workspace_products', {
          p_workspace_id: workspace.id
        });

      if (error) {
        console.error('Error loading products:', error);
        return;
      }

      setProducts(data || []);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const loadMembers = async () => {
    if (!workspace) return;

    try {
      setLoading(true);
      
      // Use RPC function to bypass RLS and get all workspace members
      const { data, error } = await supabase
        .rpc('get_workspace_members', {
          p_workspace_id: workspace.id
        });

      if (error) {
        console.error('Error loading members:', error);
        toast.error('Failed to load workspace members');
        return;
      }

      // Transform RPC result to match expected format
      const transformedData = (data || []).map((member: any) => {
        // Build the nested structure expected by the component
        const result: any = {
          id: member.id,
          workspace_id: member.member_workspace_id, // RPC returns member_workspace_id
          user_id: member.user_id,
          role: member.role,
          status: member.status,
          product_id: member.product_id,
          invited_by: member.invited_by,
          invited_at: member.invited_at,
          joined_at: member.joined_at,
          last_active_at: member.last_active_at,
          invitation_email: member.invitation_email,
        };

        // Add profile data (or placeholder for invited users)
        if (member.profile_id) {
          result.profiles = {
            id: member.profile_id,
            email: member.profile_email,
            full_name: member.profile_full_name,
            avatar_url: member.profile_avatar_url,
          };
        } else if (member.status === 'invited') {
          result.profiles = {
              id: null,
            email: member.invitation_email || 'Pending invitation',
            full_name: member.invitation_email || 'Pending',
              avatar_url: null,
          };
        }

        // Add product data if available
        if (member.product_id && member.product_name) {
          result.products = {
            id: member.product_id,
            name: member.product_name,
            color: member.product_color,
          };
        }

        return result;
      });

      setMembers(transformedData as any);
    } catch (error: any) {
      console.error('Error loading members:', error);
      toast.error('Failed to load workspace members', {
        description: error.message || 'An unexpected error occurred'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail.trim() || !inviteName.trim()) {
      toast.error('Please enter both email and name');
      return;
    }

    setIsInviting(true);

    try {
      // Get current user info for the invitation
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', currentUser?.id)
        .single();

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('You must be signed in to invite users');
        setIsInviting(false);
        return;
      }
      if (!SUPABASE_URL) {
        toast.error('Supabase project ID missing. Check environment configuration.');
        setIsInviting(false);
        return;
      }

      // The /send-invitation endpoint handles everything:
      // - Creates auth.users via inviteUserByEmail
      // - Sends the invitation email
      // - Creates profile via trigger
      // - Creates workspace_members record
      const url = `${SUPABASE_URL}/functions/v1/invitation/send-invitation`;
      console.log('📤 Sending invitation request to:', url);
      console.log('📤 Request body:', {
        inviteeEmail: inviteEmail,
        inviteeName: inviteName,
        workspaceId: workspace!.id,
        workspaceName: workspace!.name,
        role: inviteRole,
      });
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          inviteeEmail: inviteEmail,
          inviteeName: inviteName,
          workspaceId: workspace!.id,
          workspaceName: workspace!.name,
          inviterName: currentProfile?.full_name || currentUser?.email,
          role: inviteRole,
        }),
      });
      
      console.log('📥 Response status:', response.status, response.statusText);
      console.log('📥 Response headers:', Object.fromEntries(response.headers.entries()));

      // Check content type before parsing
      const contentType = response.headers.get('content-type');
      let result: any;
      
      if (contentType && contentType.includes('application/json')) {
        try {
          result = await response.json();
        } catch (parseError) {
          console.error('Failed to parse JSON response:', parseError);
          const text = await response.text();
          console.error('Raw response:', text);
          toast.error('Invalid response from server', {
            description: 'The server returned an invalid response. Please check the console for details.'
          });
          setIsInviting(false);
          return;
        }
      } else {
        // Not JSON - get the raw text
        const text = await response.text();
        console.error('Non-JSON response from server:', text);
        toast.error('Server error', {
          description: 'The server returned an unexpected response. Please check the Edge Function logs.'
        });
        setIsInviting(false);
        return;
      }
      
      if (!response.ok) {
        console.error('Failed to send invitation:', result);
        const details = result?.details ?? result?.error ?? '';
        const isRateLimit = response.status === 429 || /rate limit|too many requests/i.test(details);

        if (isRateLimit) {
          toast.error('Email rate limit reached', {
            description: 'Too many invitation emails sent in a short time. Please wait a few minutes and try again, or check your email provider limits (e.g. Resend free tier).',
            duration: 8000,
          });
        } else if (result?.error === 'Invitation already sent' || result?.error === 'User already exists') {
          toast.error(`Failed to send invitation: ${result.error}`, {
            description: result.details || result.hint || 'The user may have already been invited',
            duration: 6000,
          });
        } else {
          toast.error(result?.error || 'Failed to send invitation', {
            description: details || 'Please try again',
          });
        }

        setIsInviting(false);
        return;
      }

      console.log('✅ Invitation sent successfully:', result);
      
      toast.success('User invited successfully!', {
        description: `${inviteName} has been invited as ${getRoleDisplayName(inviteRole)}`
      });

      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteName('');
      setInviteRole('viewer');
      setInviteProductId('all');
      loadMembers();
      
    } catch (error: any) {
      console.error('Error inviting user:', error);
      toast.error('Failed to invite user', {
        description: error.message || 'An unexpected error occurred'
      });
    } finally {
      setIsInviting(false);
    }
  };

  const handleResendInvitation = async (member: WorkspaceMember) => {
    if (!workspace) {
      toast.error('Workspace context missing');
      return;
    }

    // invitation_email is cleared by the handle_new_user trigger once the
    // auth user is created, so fall back to the profile email.
    const recipientEmail = member.invitation_email || member.profiles?.email;

    if (!recipientEmail) {
      toast.error('This invited member does not have an email address to resend to.');
      return;
    }

    try {
      setResendingInvitationId(member.id);
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();

      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', currentUser?.id)
        .single();

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        toast.error('You must be signed in to resend invitations');
        return;
      }
      if (!SUPABASE_URL) {
        toast.error('Supabase project ID missing. Check environment configuration.');
        return;
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/invitation/send-invitation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          inviteeEmail: recipientEmail,
          inviteeName: member.profiles?.full_name || recipientEmail,
          workspaceId: workspace.id,
          workspaceName: workspace.name,
          inviterName: currentProfile?.full_name || currentUser?.email,
          role: member.role,
        }),
      });

      const contentType = response.headers.get('content-type');
      let result: any = null;

      if (contentType && contentType.includes('application/json')) {
        result = await response.json();
      } else {
        const text = await response.text();
        throw new Error(text || 'Unexpected response from server');
      }

      if (!response.ok) {
        throw new Error(result?.details || result?.error || 'Failed to resend invitation');
      }

      toast.success(`Invitation resent to ${recipientEmail}`);
      loadMembers();
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      toast.error('Failed to resend invitation', {
        description: error.message || 'Please try again later',
      });
    } finally {
      setResendingInvitationId(null);
    }
  };

  const handleUpdateMember = async () => {
    if (!editingMember) return;

    setIsUpdatingMember(true);

    try {
      const updateData: any = { role: editingMember.newRole };
      
      // Only admins can update product assignments
      if (isAdmin) {
        // Admins always get access to all products (NULL)
        if (editingMember.newRole === 'admin') {
          updateData.product_id = null;
        } else {
          updateData.product_id = editingMember.newProductId || null;
        }
      }
      
      const { error } = await supabase
        .from('workspace_members')
        .update(updateData)
        .eq('id', editingMember.memberId);

      if (error) {
        console.error('Error updating member:', error);
        toast.error('Failed to update user');
        return;
      }

      toast.success('User updated successfully');
      setEditingMember(null);
      loadMembers();
    } catch (error) {
      console.error('Error updating member:', error);
      toast.error('Failed to update user');
    } finally {
      setIsUpdatingMember(false);
    }
  };

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    try {
      const { error } = await supabase
        .from('workspace_members')
        .delete()
        .eq('id', memberId);

      if (error) {
        console.error('Error removing member:', error);
        toast.error('Failed to remove user');
        return;
      }

      toast.success(`${memberName} has been removed from the workspace`);
      loadMembers();
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error('Failed to remove user');
    }
  };

  const filteredMembers = members.filter(member => {
    const profile = member.profiles;
    const matchesSearch = profile.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         profile.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || member.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || member.status === statusFilter;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'invited': return 'outline';
      case 'suspended': return 'destructive';
      default: return 'outline';
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDateWithRelative = (date: string | null) => {
    if (!date) return 'Never';
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(d, regionalSettings);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-[#245F73]" />
      </div>
    );
  }

  const stats = {
    total: members.length,
    active: members.filter(m => m.status === 'active').length,
    invited: members.filter(m => m.status === 'invited').length,
    admins: members.filter(m => m.role === 'admin').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1>User Management</h1>
          <p className="text-muted-foreground">Manage users, roles, and permissions for your workspace</p>
        </div>
        {hasPermission(userRole, 'canManageUsers') && (
          <Sheet open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
            <SheetTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Invite User
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Invite New User</SheetTitle>
                <SheetDescription>Send an invitation to join your workspace</SheetDescription>
              </SheetHeader>
              <SheetBody>
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="inviteName">Full Name</Label>
                    <Input 
                      id="inviteName" 
                      placeholder="John Doe"
                      value={inviteName}
                      onChange={(e) => setInviteName(e.target.value)}
                      disabled={isInviting}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="inviteEmail">Email Address</Label>
                    <Input 
                      id="inviteEmail" 
                      type="email" 
                      placeholder="user@company.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      disabled={isInviting}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="inviteRole">Role</Label>
                    <Select value={inviteRole} onValueChange={(value) => setInviteRole(value as UserRole)} disabled={isInviting}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="viewer">Viewer</SelectItem>
                        <SelectItem value="contributor">Contributor</SelectItem>
                        <SelectItem value="product_manager">Product Manager</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {isAdmin && inviteRole !== 'admin' && products.length > 0 && (
                    <div className="space-y-2">
                      <Label htmlFor="inviteProduct">Product Assignment</Label>
                      <Select value={inviteProductId} onValueChange={setInviteProductId} disabled={isInviting}>
                        <SelectTrigger>
                          <SelectValue placeholder="All products (default)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All products</SelectItem>
                          {products.map(product => (
                            <SelectItem key={product.id} value={product.id}>
                              {product.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Admins can change this later. Leave empty for access to all products.
                      </p>
                    </div>
                  )}
                </div>
              </SheetBody>
              <SheetFooter>
                <Button variant="outline" onClick={() => setInviteDialogOpen(false)} disabled={isInviting}>
                  Cancel
                </Button>
                <Button onClick={handleInviteUser} disabled={isInviting}>
                  {isInviting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Inviting...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Send Invitation
                    </>
                  )}
                </Button>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Pending Invites</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.invited}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Admins</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.admins}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="product_manager">Product Manager</SelectItem>
                  <SelectItem value="contributor">Contributor</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="invited">Invited</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Users ({filteredMembers.length})</CardTitle>
          <CardDescription>Manage workspace members and their permissions</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Active</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMembers.map((member) => {
                const profile = member.profiles;
                const config = roleConfig[member.role];
                const isInvited = member.status === 'invited';
                const hasNoProfile = !profile || profile.full_name === 'Pending';
                
                return (
                  <TableRow 
                    key={member.id}
                    className={isInvited && hasNoProfile ? 'opacity-75 bg-muted/30' : ''}
                  >
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={profile?.avatar_url || ''} />
                          <AvatarFallback>{getInitials(profile?.full_name || 'P')}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {profile?.full_name || 'Pending'}
                            {isInvited && hasNoProfile && (
                              <span className="text-xs text-muted-foreground" title="Invitation sent but not yet accepted">
                                ⏳
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">{profile?.email || member.invitation_email || 'No email'}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        {config.icon}
                        <Badge variant={config.color}>
                          {config.label}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      {member.product_id && member.products ? (
                        <Badge variant="outline" style={{ borderColor: member.products.color }}>
                          {member.products.name}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">All products</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusColor(member.status)}>
                        {member.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {formatDateWithRelative(member.last_active_at)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {member.status === 'invited' && member.invited_at
                          ? formatDate(member.invited_at, regionalSettings)
                          : formatDate(member.joined_at, regionalSettings)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Sheet>
                          <SheetTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={() => setSelectedMember(member)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </SheetTrigger>
                          <SheetContent>
                            <SheetHeader>
                              <SheetTitle>User Details</SheetTitle>
                              <SheetDescription>View member information</SheetDescription>
                            </SheetHeader>
                            <SheetBody>
                              {selectedMember && (
                                <div className="space-y-4">
                                  <div className="flex items-center space-x-4">
                                    <Avatar className="h-16 w-16">
                                      <AvatarImage src={selectedMember.profiles.avatar_url || ''} />
                                      <AvatarFallback className="text-lg">
                                        {getInitials(selectedMember.profiles.full_name)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <h3 className="text-lg font-medium">{selectedMember.profiles.full_name}</h3>
                                      <p className="text-muted-foreground text-sm">{selectedMember.profiles.email}</p>
                                      <div className="flex items-center space-x-2 mt-2">
                                        <Badge variant={roleConfig[selectedMember.role].color}>
                                          {roleConfig[selectedMember.role].label}
                                        </Badge>
                                        <Badge variant={getStatusColor(selectedMember.status)}>
                                          {selectedMember.status}
                                        </Badge>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                                    <div>
                                      <Label className="text-xs text-muted-foreground">Last Active</Label>
                                      <p className="text-sm">
                                        {formatDateWithRelative(selectedMember.last_active_at)}
                                      </p>
                                    </div>
                                    <div>
                                      <Label className="text-xs text-muted-foreground">Joined</Label>
                                      <p className="text-sm">
                                        {formatDate(selectedMember.joined_at, regionalSettings)}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </SheetBody>
                          </SheetContent>
                        </Sheet>
                        
                        {hasPermission(userRole, 'canManageUsers') && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {isInvited && (
                                <>
                                  <DropdownMenuItem 
                                    disabled={resendingInvitationId === member.id}
                                    onClick={(event) => {
                                      event.preventDefault();
                                      handleResendInvitation(member);
                                    }}
                                  >
                                    {resendingInvitationId === member.id ? (
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                    <Mail className="h-4 w-4 mr-2" />
                                    )}
                                    {resendingInvitationId === member.id ? 'Resending...' : 'Resend Invitation'}
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                </>
                              )}
                              <DropdownMenuItem onClick={() => setEditingMember({ memberId: member.id, newRole: member.role, newProductId: member.product_id })}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit User
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <DropdownMenuItem 
                                    className="text-destructive focus:text-destructive"
                                    onSelect={(e) => e.preventDefault()}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Remove User
                                  </DropdownMenuItem>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Remove User</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to remove {profile.full_name} from the workspace? This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction 
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      onClick={() => handleRemoveMember(member.id, profile.full_name)}
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Remove User
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Role permissions reference */}
      <RolePermissionsMatrix />

      {/* Edit Member Flyout */}
      <Sheet open={!!editingMember} onOpenChange={(open) => !open && setEditingMember(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Edit User</SheetTitle>
            <SheetDescription>
              Update the role and product assignment for this workspace member
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Role</Label>
                <Select 
                  value={editingMember?.newRole} 
                  onValueChange={(value) => setEditingMember(prev => prev ? { ...prev, newRole: value as UserRole } : null)}
                  disabled={isUpdatingMember}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="contributor">Contributor</SelectItem>
                    <SelectItem value="product_manager">Product Manager</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {isAdmin && editingMember?.newRole !== 'admin' && (
                <div className="space-y-2">
                  <Label>Product Assignment</Label>
                  <Select 
                    value={editingMember?.newProductId || 'all'} 
                    onValueChange={(value) => setEditingMember(prev => prev ? { ...prev, newProductId: value === 'all' ? null : value } : null)}
                    disabled={isUpdatingMember}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Products</SelectItem>
                      {products.map(product => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </SheetBody>
          <SheetFooter>
            <Button variant="outline" onClick={() => setEditingMember(null)} disabled={isUpdatingMember}>
              Cancel
            </Button>
            <Button onClick={handleUpdateMember} disabled={isUpdatingMember}>
              {isUpdatingMember ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update User'
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
